import { Injectable } from '@angular/core';
import Swal from 'sweetalert2';
import { AutoRefillService } from '../../../services/auto-refill.service';
import { SharedService } from '../../../services/shared.service';
import {
  buildScheduleFreq,
  parseScheduleFreqForm,
  resolveScheduleDays,
  pickDto,
  pickScheduleId
} from './auto-refill.utils';
import { AUTO_REFILL_FREQ_PRESETS } from './auto-refill.constants';
import {
  buildCycleOptionsHtml,
  buildEditScheduleFormHtml,
  buildFreqOptionsHtml,
  buildHistoryHtml
} from './auto-refill-dialog.helpers';

/** SweetAlert2 dialogs for Auto Refill. */
@Injectable({
  providedIn: 'root'
})
export class AutoRefillDialogService {

  constructor(
    private autoRefillService: AutoRefillService,
    private sharedService: SharedService
  ) { }

  openConfirm(row: any, onCompleted?: () => void): void {
    var userId = this.sharedService.getUserId();
    var scheduleId = pickScheduleId(row);
    if (!userId || scheduleId <= 0) {
      return;
    }

    var self = this;
    var brandName = String(pickDto(row, 'brandName', 'BrandName') || '');

    this.showLoadingSwal('Loading...');

    this.autoRefillService.getCycleOptions(userId, scheduleId).subscribe(
      function (opts) {
        Swal.close();
        var cycleOptions = opts || [];
        if (cycleOptions.length === 0) {
          self.showSwalError('Could not load cycle dates.');
          return;
        }
        self.showConfirmRefillDialog(row, brandName, cycleOptions, onCompleted);
      },
      function () {
        Swal.close();
        self.showSwalError('Could not load cycle dates.');
      }
    );
  }

  openDeactivate(row: any, onCompleted?: () => void): void {
    var brandName = String(pickDto(row, 'brandName', 'BrandName') || '');
    var self = this;

    Swal.fire({
      icon: 'warning',
      title: 'Confirm Deactivation',
      html:
        '<p>Deactivate schedule for <strong>' + this.escapeHtml(brandName) + '</strong>?</p>' +
        '<p class="text-muted small mb-0">This removes the medication from active auto refills.</p>',
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Yes, Deactivate',
      confirmButtonColor: '#dc3545'
    }).then(function (result) {
      if (result.value) {
        self.submitDeactivate(row, onCompleted);
      }
    });
  }

  openEditSchedule(row: any, onCompleted?: () => void, title?: string): void {
    var scheduleId = pickScheduleId(row);
    var userId = this.sharedService.getUserId();
    if (!userId || scheduleId <= 0) {
      return;
    }

    var self = this;
    var dialogTitle = title && String(title).trim() ? String(title).trim() : 'Edit Schedule';
    var brandName = String(pickDto(row, 'brandName', 'BrandName') || '');
    var currentSchedule = String(pickDto(row, 'scheduleFreq', 'ScheduleFreq') || '');
    var currentScheduleDays = parseInt(String(pickDto(row, 'scheduleDays', 'ScheduleDays') || '0'), 10);
    var currentNotes = String(pickDto(row, 'notes', 'Notes') || '');
    var currentFirstFill = String(pickDto(row, 'firstFillRaw', 'FirstFillRaw') || '');
    if (!currentFirstFill) {
      currentFirstFill = this.toDateInputValue(String(pickDto(row, 'firstFill', 'FirstFill') || ''));
    }
    var freqForm = parseScheduleFreqForm(currentSchedule, currentScheduleDays);
    var freqOptionsHtml = buildFreqOptionsHtml(freqForm.preset);
    var customWrapDisplay = freqForm.preset === 'Custom' ? 'block' : 'none';

    Swal.fire({
      title: dialogTitle,
      width: 480,
      customClass: {
        popup: 'ar-edit-schedule-swal'
      },
      html: buildEditScheduleFormHtml(
        brandName,
        freqOptionsHtml,
        customWrapDisplay,
        freqForm.customDays,
        currentFirstFill
      ),
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Save',
      focusConfirm: false,
      onOpen: function () {
        var notesEl = document.getElementById('arEditNotes') as HTMLTextAreaElement;
        if (notesEl) {
          notesEl.value = currentNotes;
        }
        var freqEl = document.getElementById('arEditFreq') as HTMLSelectElement;
        var customWrap = document.getElementById('arEditCustomWrap');
        if (freqEl && customWrap) {
          freqEl.addEventListener('change', function () {
            customWrap.style.display = freqEl.value === 'Custom' ? 'block' : 'none';
          });
        }
      },
      preConfirm: function () {
        var preset = self.readEditFreqPreset();
        var customDays = self.readEditCustomDays(freqForm.customDays);
        if (preset === 'Custom' && customDays <= 0) {
          Swal.showValidationMessage('Please enter valid custom days.');
          return false;
        }
        var firstFill = self.readEditFirstFill();
        if (!firstFill) {
          Swal.showValidationMessage('Please select a 1st Fill Date.');
          return false;
        }
        var notesEl = document.getElementById('arEditNotes') as HTMLTextAreaElement;
        var notes = notesEl ? String(notesEl.value || '').trim() : '';
        var scheduleDays = resolveScheduleDays(preset, customDays);
        if (scheduleDays <= 0) {
          Swal.showValidationMessage('Please enter a valid schedule frequency.');
          return false;
        }
        return {
          scheduleFreq: buildScheduleFreq(preset, customDays),
          scheduleDays: scheduleDays,
          firstFill: firstFill,
          notes: notes
        };
      }
    } as any).then(function (result) {
      if (result.value) {
        var payload = result.value as any;
        self.submitUpdateSchedule(
          scheduleId,
          payload.scheduleFreq,
          payload.scheduleDays,
          payload.firstFill,
          payload.notes,
          onCompleted);
      }
    });
  }

  openHistory(row: any): void {
    var userId = this.sharedService.getUserId();
    var scheduleId = pickScheduleId(row);
    if (!userId || scheduleId <= 0) {
      return;
    }

    var self = this;
    var brandName = String(pickDto(row, 'brandName', 'BrandName') || '');
    var schedule = String(pickDto(row, 'scheduleFreq', 'ScheduleFreq') || '');
    var firstFill = String(pickDto(row, 'firstFill', 'FirstFill') || '');

    this.showLoadingSwal('Loading...');

    this.autoRefillService.getRefillHistory(userId, scheduleId).subscribe(
      function (data) {
        Swal.close();
        var historyRows = data || [];
        Swal.fire({
          title: 'Refill History',
          width: 720,
          html: buildHistoryHtml(brandName, schedule, firstFill, historyRows),
          confirmButtonText: 'Close'
        });
      },
      function () {
        Swal.close();
        self.showSwalError('Could not load history.');
      }
    );
  }

  private showConfirmRefillDialog(
    row: any,
    brandName: string,
    cycleOptions: any[],
    onCompleted?: () => void
  ): void {
    var self = this;
    var optionsHtml = buildCycleOptionsHtml(cycleOptions);
    var defaultCycle = String(pickDto(cycleOptions[0], 'value', 'Value') || '');
    var nextFill = String(pickDto(row, 'nextFill', 'NextFill') || '');

    var btnBaseStyle =
      'display:block;flex:1;margin:0;padding:0.65em 0.85em;font-size:0.9em;' +
      'font-weight:600;line-height:1.35;color:#fff;border:none;border-radius:0.25em;' +
      'cursor:pointer;box-shadow:0 0 0 1px rgba(0,0,0,0.05);text-align:center;';

    Swal.fire({
      title: 'Confirm Refill Box',
      icon: 'question',
      width: 720,
      customClass: {
        popup: 'ar-confirm-refill-swal'
      },
      html:
        '<p class="mb-3 text-left">Confirm refill for <strong>' + this.escapeHtml(brandName) + '</strong> on <strong>' + this.escapeHtml(nextFill) + '</strong>?</p>' +
        '<label class="small font-weight-bold d-block text-left mb-1" for="arSwalCycle">' +
        'Fill completed up to cycle</label>' +
        '<select id="arSwalCycle" class="swal2-input" style="width:100%;margin:0 0 1rem;box-sizing:border-box;">' +
        optionsHtml + '</select>' +
        '<div id="arSwalActions" style="display:flex;flex-direction:row;justify-content:center;gap:0.5rem;width:100%;">' +
        '<button type="button" id="arSwalYes" style="' + btnBaseStyle + 'background-color:#28a745;">' +
        'Filled</button>' +
        '<button type="button" id="arSwalNo" style="' + btnBaseStyle + 'background-color:#dc3545;">' +
        'Rescheduled</button>' +
        '<button type="button" id="arSwalCancel" style="' + btnBaseStyle + 'background-color:#6c757d;">' +
        'Exit</button>' +
        '</div>',
      showConfirmButton: false,
      showCancelButton: false,
      focusConfirm: false,
      onOpen: function () {
        var yesBtn = document.getElementById('arSwalYes');
        var noBtn = document.getElementById('arSwalNo');
        var cancelBtn = document.getElementById('arSwalCancel');

        if (yesBtn) {
          yesBtn.addEventListener('click', function () {
            var cycle = self.readSelectedCycle(defaultCycle);
            if (!cycle) {
              Swal.showValidationMessage('Please select a cycle.');
              return;
            }
            Swal.close();
            self.submitCompleteRefill(row, cycle, onCompleted);
          });
        }

        if (noBtn) {
          noBtn.addEventListener('click', function () {
            Swal.close();
            setTimeout(function () {
              self.openEditSchedule(row, onCompleted, 'Reschedule');
            }, 120);
          });
        }

        if (cancelBtn) {
          cancelBtn.addEventListener('click', function () {
            Swal.close();
          });
        }
      }
    } as any);
  }

  private showNotAdministeredDialog(row: any, cycleValue: string, onCompleted?: () => void): void {
    var self = this;

    Swal.fire({
      title: 'No, not able to Refill & Reason',
      width: 560,
      customClass: {
        popup: 'ar-fail-refill-reason-swal'
      },
      input: 'textarea',
      inputPlaceholder: 'Ex: Patient in hospital...',
      inputAttributes: {
        rows: '3'
      },
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Save Note',
      inputValidator: function (value) {
        if (!value || !String(value).trim()) {
          return 'Please write a reason.';
        }
        return null;
      }
    }).then(function (result) {
      if (result.value) {
        self.submitFailRefill(row, cycleValue, String(result.value).trim(), onCompleted);
      }
    });
  }

  private submitCompleteRefill(row: any, cycleValue: string, onCompleted?: () => void): void {
    var userId = this.sharedService.getUserId();
    var scheduleId = pickScheduleId(row);
    var body = {
      scheduleId: scheduleId,
      startDate: pickDto(row, 'nextFillRaw', 'NextFillRaw'),
      completedDate: cycleValue
    };
    var self = this;

    this.showLoadingSwal('Saving...');

    this.autoRefillService.completeRefill(userId, body).subscribe(
      function (res) {
        Swal.close();
        if (res && res.success) {
          Swal.fire({
            icon: 'success',
            title: 'Saved',
            text: 'Refill marked as administered.',
            confirmButtonText: 'OK'
          });
          self.notifyCompleted(onCompleted);
          return;
        }
        self.showSwalError((res && res.message) ? res.message : 'Could not save.');
      },
      function () {
        Swal.close();
        self.showSwalError('Could not save administered refills.');
      }
    );
  }

  private submitFailRefill(
    row: any,
    cycleValue: string,
    note: string,
    onCompleted?: () => void
  ): void {
    var userId = this.sharedService.getUserId();
    var scheduleId = pickScheduleId(row);
    var body = {
      scheduleId: scheduleId,
      note: note,
      startDate: pickDto(row, 'nextFillRaw', 'NextFillRaw'),
      completedDate: cycleValue
    };
    var self = this;

    this.showLoadingSwal('Saving...');

    this.autoRefillService.failRefill(userId, body).subscribe(
      function (res) {
        Swal.close();
        if (res && res.success) {
          Swal.fire({
            icon: 'success',
            title: 'Saved',
            text: 'Failure note recorded.',
            confirmButtonText: 'OK'
          });
          self.notifyCompleted(onCompleted);
          return;
        }
        self.showSwalError((res && res.message) ? res.message : 'Could not save.');
      },
      function () {
        Swal.close();
        self.showSwalError('Could not save failure note.');
      }
    );
  }

  private submitUpdateSchedule(
    scheduleId: number,
    scheduleFreq: string,
    scheduleDays: number,
    firstFill: string,
    notes: string,
    onCompleted?: () => void
  ): void {
    var userId = this.sharedService.getUserId();
    var body = {
      scheduleId: scheduleId,
      scheduleFreq: scheduleFreq,
      scheduleDays: scheduleDays,
      firstFill: firstFill,
      notes: notes
    };
    var self = this;

    this.showLoadingSwal('Saving...');

    this.autoRefillService.updateSchedule(userId, body).subscribe(
      function (res) {
        Swal.close();
        if (res && (res.success || res.Success)) {
          Swal.fire({
            icon: 'success',
            title: 'Saved',
            text: 'Schedule updated successfully.',
            confirmButtonText: 'OK'
          });
          self.notifyCompleted(onCompleted);
          return;
        }
        self.showSwalError((res && res.message) ? res.message : 'Could not update schedule.');
      },
      function () {
        Swal.close();
        self.showSwalError('Could not update schedule.');
      }
    );
  }

  private submitDeactivate(row: any, onCompleted?: () => void): void {
    var userId = this.sharedService.getUserId();
    var scheduleId = pickScheduleId(row);
    var self = this;

    this.showLoadingSwal('Deactivating...');

    this.autoRefillService.deactivateSchedule(userId, scheduleId).subscribe(
      function (res) {
        Swal.close();
        if (res && res.success) {
          Swal.fire({
            icon: 'success',
            title: 'Deactivated',
            text: 'Schedule removed from active auto refills.',
            confirmButtonText: 'OK'
          });
          self.notifyCompleted(onCompleted);
          return;
        }
        self.showSwalError((res && res.message) ? res.message : 'Could not deactivate.');
      },
      function () {
        Swal.close();
        self.showSwalError('Could not deactivate schedule.');
      }
    );
  }

  private notifyCompleted(onCompleted?: () => void): void {
    if (onCompleted) {
      onCompleted();
    }
  }

  private showLoadingSwal(title: string): void {
    Swal.fire({
      title: title,
      allowOutsideClick: false,
      onOpen: function () {
        Swal.showLoading();
      }
    } as any);
  }

  showSwalError(message: string): void {
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: message,
      confirmButtonText: 'OK'
    });
  }

  private readSelectedCycle(fallback: string): string {
    var select = document.getElementById('arSwalCycle') as HTMLSelectElement;
    if (select && select.value) {
      return select.value;
    }
    return fallback;
  }

  private readEditFreqPreset(): string {
    var select = document.getElementById('arEditFreq') as HTMLSelectElement;
    if (select && select.value) {
      return select.value;
    }
    return AUTO_REFILL_FREQ_PRESETS[0].id;
  }

  private readEditCustomDays(fallback: number): number {
    var input = document.getElementById('arEditCustomDays') as HTMLInputElement;
    if (!input || !input.value) {
      return fallback;
    }
    var days = parseInt(String(input.value), 10);
    if (isNaN(days)) {
      return fallback;
    }
    return days;
  }

  private readEditFirstFill(): string {
    var input = document.getElementById('arEditFirstFill') as HTMLInputElement;
    if (!input || !input.value) {
      return '';
    }
    var value = String(input.value).trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return '';
    }
    return value;
  }

  /** Convert MM/dd/yyyy display date to yyyy-MM-dd for input type=date. */
  private toDateInputValue(displayDate: string): string {
    var text = String(displayDate || '').trim();
    if (!text || text === 'N/A') {
      return '';
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
      return text;
    }
    var parts = text.split('/');
    if (parts.length !== 3) {
      return '';
    }
    var month = parts[0];
    var day = parts[1];
    var year = parts[2];
    if (month.length === 1) {
      month = '0' + month;
    }
    if (day.length === 1) {
      day = '0' + day;
    }
    if (year.length !== 4 || month.length !== 2 || day.length !== 2) {
      return '';
    }
    return year + '-' + month + '-' + day;
  }

  private escapeHtml(value: string): string {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
